import OnboardingDialog from '../../../common/onboarding-dialog';
import l from '../../../../lang';
import CollapsibleCard from '../../../common/collapsible-card';
import { ReactComponent as FileIcon } from '../../../../assets/icons/file.svg';
import Card from '../../../common/card';
import KYCUploadFile from '../../../common/kyc-upload-file';
import { useCompanyInfo } from '../../../../store/company-information';
import { useCallback, useEffect, useState } from 'react';
import { needInternationalWireDetails } from '../../../../utils/bank-account-utils';
import { useDashboard } from '../../../../store/dashboard';
import { useBankAccounts } from '../../../../store/bank-accounts';
import labels from '../kyc-levels/config/labels';
import { useFeature } from '../../../../hooks/use-features';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import { Routes } from '../../../../config/routes';
import Loading from '../../../common/loading';

interface DocsRequired {
  bankAccountDocument?: boolean;
  identityDocument?: boolean;
  proofOfAddressDocument?: boolean;
  taxCompliantDocument?: boolean;
  shareholdersDocument?: boolean;
  internationalWireDetails?: boolean;
}

interface DocUpload {
  name: string;
  file: string;
}

const reviewDocumentation = () => {
  const { getKycDocuments, kycDocuments, uploadKycDocuments, loading, success } = useCompanyInfo();
  const { merchantMe } = useDashboard();
  const { allBankAccounts, getBankAccounts } = useBankAccounts();
  const [docsRequired, setDocsRequired] = useState<DocsRequired>({});
  const { hasFeature } = useFeature();
  const [documentPayload, setDocumentPayload] = useState<DocUpload[]>([]);

  const { company: isCompany, country, documentType, name, lastName } = merchantMe || { company: false };

  useEffect(() => {
    getBankAccounts();
    getKycDocuments();
  }, []);

  useEffect(() => {
    if (success) {
      setDocumentPayload([]);
      location.href = Routes.HOME;
    }
  }, [success]);

  const findDocument = (type: string) => {
    return kycDocuments?.find(doc => doc.type === type);
  };

  const handleDocument = (type: string, file: string | null) => {
    if (file) {
      setDocumentPayload(prev => [...prev, { name: type, file }]);
    }
  };

  useEffect(() => {
    setDocsRequired({
      bankAccountDocument: !!findDocument('BANK_ACCOUNT'),
      identityDocument: !!findDocument('IDENTITY'),
      proofOfAddressDocument: !!findDocument('PROOF_OF_ADDRESS'),
      taxCompliantDocument: !!findDocument('TAX_COMPLIANCE'),
      shareholdersDocument: !!findDocument('SHAREHOLDERS'),
      internationalWireDetails: !!needInternationalWireDetails(
        merchantMe?.country ?? '',
        allBankAccounts?.data[0]?.bankCode
      ),
    });
  }, [kycDocuments, allBankAccounts, merchantMe]);

  const getInitialFile = useCallback(
    (type: string): File | null => {
      const doc = kycDocuments?.find(d => d.type === type);
      const filePaths = doc?.file_path ? doc.file_path.split('/') : [];
      return doc && doc.file_path ? new File([doc.file_path], filePaths[filePaths.length - 1]) : null;
    },
    [kycDocuments]
  );

  const handleSubmit = () => {
    const requiredDocs = kycDocuments?.filter(doc => doc.status === 'REJECTED').map(doc => doc.type) || [];
    const remainingDocs = [] as string[];

    requiredDocs.forEach(type => {
      const payload = documentPayload.find(p => p.name === type);
      if (!payload) {
        remainingDocs.push(type);
      }
    });

    if (remainingDocs.length > 0) {
      console.error('Missing documents:', remainingDocs);
      return;
    }

    console.log('Submitting documents:', documentPayload);

    uploadKycDocuments(documentPayload);
  };

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      title={`${l('companyInformation.onboarding.title.part1')} | ${l('companyInformation.kycTwoMenu.documentation')}`}
      body={
        <>
          {loading ? (
            <div style={{ marginTop: '6em' }}>
              <Loading />
            </div>
          ) : (
            <div style={{ marginTop: '6em', marginLeft: '1em', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <Card>
                <h2>{l('companyInformation.kycTwoMenu.documentation')}</h2>
                <p>{l('companyInformation.kycTwoMenu.documentation.desc')}</p>
                {(docsRequired.taxCompliantDocument || docsRequired.shareholdersDocument) && (
                  <CollapsibleCard
                    title={l('companyInformation.kycTwoMenu.companyData')}
                    defaultExpanded={true}
                    icon={<FileIcon />}
                  >
                    {docsRequired.taxCompliantDocument && (
                      <KYCUploadFile
                        title={labels.step2TaxCompliantDocument(hasFeature, { isCompany, country })}
                        description={labels.step2TaxCompliantDocumentDescription(hasFeature, {
                          isCompany,
                          documentType,
                          name,
                          lastName,
                        })}
                        document={findDocument('TAX_COMPLIANCE')}
                        supportedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        initialFile={getInitialFile('TAX_COMPLIANCE')}
                        onChange={({ base64 }) => {
                          handleDocument('TAX_COMPLIANCE', base64);
                        }}
                        externalError={''}
                        maxSize={5}
                      />
                    )}
                    {docsRequired.shareholdersDocument && (
                      <KYCUploadFile
                        title={labels.step2ShareholdersDocument(hasFeature, { isCompany, country })}
                        description={labels.step2ShareholdersDocumentHelp(hasFeature, { isCompany, country })}
                        supportedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        initialFile={getInitialFile('SHAREHOLDERS')}
                        document={findDocument('SHAREHOLDERS')}
                        onChange={({ base64 }) => {
                          handleDocument('SHAREHOLDERS', base64);
                        }}
                        externalError={''}
                        maxSize={5}
                      />
                    )}
                  </CollapsibleCard>
                )}
                {docsRequired.bankAccountDocument && (
                  <CollapsibleCard
                    title={l('companyInformation.kycTwo.bankAccountDocumentation.title')}
                    defaultExpanded={true}
                    icon={<FileIcon />}
                  >
                    {docsRequired.bankAccountDocument && (
                      <KYCUploadFile
                        title={labels.step2BankAccountDocumentation(hasFeature, { isCompany })}
                        description={labels.step2BankAccountDocumentationHelp(hasFeature, { isCompany, name, lastName })}
                        supportedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        initialFile={getInitialFile('BANK_ACCOUNT')}
                        document={findDocument('BANK_ACCOUNT')}
                        onChange={({ base64 }) => {
                          handleDocument('BANK_ACCOUNT', base64);
                        }}
                        externalError={''}
                        maxSize={5}
                      />
                    )}
                  </CollapsibleCard>
                )}
                {(docsRequired.identityDocument || docsRequired.proofOfAddressDocument) && (
                  <CollapsibleCard
                    title={l('companyInformation.kycTwo.companyOwnerDocuments')}
                    defaultExpanded={true}
                    icon={<FileIcon />}
                  >
                    {docsRequired.identityDocument && (
                      <KYCUploadFile
                        title={labels.step2IdentityDocument(hasFeature, { isCompany, documentType })}
                        description={labels.step2IdentityDocumentHelp(hasFeature, { isCompany, name, lastName })}
                        supportedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        initialFile={getInitialFile('IDENTITY')}
                        document={findDocument('IDENTITY')}
                        onChange={({ base64 }) => {
                          handleDocument('IDENTITY', base64);
                        }}
                        externalError={''}
                        maxSize={5}
                      />
                    )}
                    {docsRequired.proofOfAddressDocument && (
                      <KYCUploadFile
                        title={labels.step2ProofOfAddressDocument(hasFeature, { isCompany })}
                        description={labels.step2ProofOfAddressDocumentHelp(hasFeature, {
                          isCompany,
                          country,
                          name,
                          lastName,
                        })}
                        supportedExtensions={['pdf', 'jpg', 'jpeg', 'png']}
                        initialFile={getInitialFile('PROOF_OF_ADDRESS')}
                        document={findDocument('PROOF_OF_ADDRESS')}
                        onChange={({ base64 }) => {
                          handleDocument('PROOF_OF_ADDRESS', base64);
                        }}
                        externalError={''}
                        maxSize={5}
                      />
                    )}
                  </CollapsibleCard>
                )}
                <div style={{ marginTop: '1em' }}>
                  <ButtonCustom
                    fullWidth
                    loading={loading}
                    disabled={loading || documentPayload.length === 0}
                    text={l('companyInformation.next')}
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    size="square"
                    component="label"
                  />
                </div>
              </Card>
            </div>
          )}
        </>
      }
      menuItems={[
        { title: l('companyInformation.kycTwoMenu.introduction'), completed: true, selected: false },
        {
          title: l('companyInformation.kycTwoMenu.documentation'),
          subItems: [
            {
              title: l('companyInformation.kycTwo.companyOwnerLegalInformation'),
              completed: findDocument('IDENTITY')?.status === 'APPROVED',
              selected: false,
            },
            {
              title: l('companyInformation.kycTwo.bankAccountInformation'),
              completed: findDocument('BANK_ACCOUNT')?.status === 'APPROVED',
              selected: false,
              hide: false,
            },
            {
              title: l('companyInformation.kycTwo.proofOfAddressDocument.company/physical'),
              completed: findDocument('PROOF_OF_ADDRESS')?.status === 'APPROVED',
              selected: false,
              hide: false,
            },
            {
              title: labels.step2TaxCompliantDocument(hasFeature, { isCompany, country }),
              completed: findDocument('TAX_COMPLIANCE')?.status === 'APPROVED',
              selected: false,
              hide: false,
            },
          ],
        },
      ]}
    />
  );
};

export default reviewDocumentation;
